import React, { useMemo, useCallback } from 'react';
import { Card, ConfigProvider } from 'antd';
import type { CardProps as AntCardProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { createCommand } from '../../base/actionParser';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

// Semantic DOM 样式类型
interface CardSemanticStyles {
  root?: React.CSSProperties;
  header?: React.CSSProperties;
  title?: React.CSSProperties;
  extra?: React.CSSProperties;
  cover?: React.CSSProperties;
  body?: React.CSSProperties;
  actions?: React.CSSProperties;
}

// Tab 配置项
interface TabItem {
  key: string;
  tab: string;
  disabled?: boolean;
}

// Define CardTabListType locally as it's not exported by antd consistently
type CardTabListType = {
  key: string;
  tab: React.ReactNode;
  disabled?: boolean;
};

export interface AndCardProps extends Omit<AntCardProps, 'title' | 'extra' | 'cover' | 'tabBarExtraContent' | 'actions' | 'onTabChange' | 'tabList'>, BaseComponentProps, DataSetConfig {
  /** 标题 (字符串或 ReactNode) */
  title?: React.ReactNode | string;
  /** 右上角操作区 (字符串或 ReactNode) */
  extra?: React.ReactNode | string;
  /** 封面 (ReactNode) */
  cover?: React.ReactNode;
  /** Tab bar 额外内容 (ReactNode) */
  tabBarExtraContent?: React.ReactNode;
  /** 操作组 (ReactNode[]) */
  actions?: React.ReactNode[];
  /** Tab 列表配置 */
  tabList?: TabItem[] | string;
  /** 当前激活 Tab */
  activeTabKey?: string;
  /** 默认激活 Tab */
  defaultActiveTabKey?: string;
  /** Tab 变化动作 */
  tabChangeAction?: string;
  /** 自定义 onTabChange */
  onTabChange?: (key: string) => void;
  /** Semantic DOM 样式 */
  styles?: CardSemanticStyles | string;
  /** Design Token */
  token?: Record<string, any> | string;
  /** GrapesJS 显示控制 (不传递给 DOM) */
  showHeader?: boolean;
  showCover?: boolean;
  showActions?: boolean;
}

// 解析 JSON
const parseJson = <T,>(value: T | string | undefined, fallback: T): T => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'string') {
    try {
      const trimmed = value.trim();
      if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        return JSON.parse(trimmed);
      }
    } catch {
      return fallback;
    }
  }
  return value as T;
};

// 将 CSS kebab-case 属性名转为 React camelCase（如 border-radius → borderRadius）
// 设计器通过 JSON editor 存储样式时用原生 CSS 命名，需在运行态转换才能正确传给 React style。
const kebabToCamel = (str: string): string =>
  str.replace(/-([a-z])/g, (_, c) => c.toUpperCase());

const normalizeStyleKeys = (
  obj: Record<string, any> | undefined | null
): Record<string, any> => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj ?? {};
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [kebabToCamel(k), v])
  );
};

// 解析 TabList
const parseTabList = (tabList: TabItem[] | string | undefined): CardTabListType[] => {
  const items = parseJson(tabList, [] as TabItem[]);
  return items.map(item => ({
    key: item.key,
    tab: item.tab,
    disabled: item.disabled,
  }));
};

export const AndCard: React.FC<AndCardProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  // 内容插槽
  title,
  extra,
  cover,
  tabBarExtraContent,
  actions,
  // Tab 属性
  tabList: propTabList,
  activeTabKey: propActiveTabKey,
  defaultActiveTabKey,
  tabChangeAction,
  onTabChange,
  // 样式
  styles: propStyles,
  token: propToken,
  // 过滤掉不应该传递给 Card 的 props (避免 React 警告)
  // 这些属性主要由 GrapesJS 使用或在内部逻辑处理，不需要传递给 Antd Card
  showHeader,
  showCover,
  showActions,
  // AntD v6 将 size="default" 重命名为 "medium"，显式接收以便做兼容映射
  size,
  // 其他
  children,
  ...restProps
}) => {
  const addCommand = useAndStore((state) => state.addCommand);
  const getDataSet = useAndStore((state) => state.getDataSet);
  const setDataSet = useAndStore((state) => state.setDataSet);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  // AntD v6 将 size="default" 重命名为 size="medium"，做兼容映射避免警告
  const normalizedSize = (size as string) === 'default' ? 'medium' : size;

  // 解析样式，并将 kebab-case 键名转为 camelCase（设计器 JSON 编辑器存的是原生 CSS 命名）
  const styles = useMemo(() => {
    const parsed = parseJson(propStyles, {} as CardSemanticStyles);
    return Object.fromEntries(
      Object.entries(parsed).map(([section, styleObj]) => [
        section,
        normalizeStyleKeys(styleObj as Record<string, any>),
      ])
    ) as CardSemanticStyles;
  }, [propStyles]);

  // 解析 tabList
  const tabList = useMemo(() => {
    const parsed = parseTabList(propTabList);
    return parsed.length > 0 ? parsed : undefined;
  }, [propTabList]);

  // 从 store 获取 activeTabKey
  const storeActiveKey = useMemo(() => {
    if (dataSetKey && dataSetField) {
      const value = getDataSet(dataSetKey, `${dataSetField}.activeTabKey`);
      if (typeof value === 'string') return value;
    }
    return undefined;
  }, [dataSetKey, dataSetField, getDataSet]);

  // 计算 activeTabKey
  const activeTabKey = storeActiveKey ?? propActiveTabKey;

  // Tab 切换处理
  const handleTabChange = useCallback((key: string) => {
    if (onTabChange) {
      onTabChange(key);
      return;
    }
    // 更新 store
    if (dataSetKey && dataSetField) {
      setDataSet(dataSetKey, `${dataSetField}.activeTabKey`, key);
    }
    // 触发动作
    if (tabChangeAction && sessionId) {
      addCommand(sessionId, createCommand(tabChangeAction, { key }));
    }
  }, [onTabChange, dataSetKey, dataSetField, setDataSet, tabChangeAction, sessionId, addCommand]);

  // 从 children 中提取插槽内容 (兼容 GrapesJS 导出结构)
  const { titleSlot, extraSlot, coverSlot, actionsSlot, otherChildren, actionsFromChildren } = useMemo(() => {
    let titleSlot: React.ReactNode = null;
    let extraSlot: React.ReactNode = null;
    let coverSlot: React.ReactNode = null;
    let actionsSlot: React.ReactNode[] = [];
    const otherChildren: React.ReactNode[] = [];
    // 标记 actions 是否来自 children slot（而非 actions prop）
    let actionsFromChildren = false;

    // 优先使用 props 传入的内容
    if (title) titleSlot = title;
    if (extra) extraSlot = extra;
    if (cover) coverSlot = cover;
    if (actions) actionsSlot = actions;

    // 如果 props 没有提供，尝试从 children 中提取
    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child)) {
        otherChildren.push(child);
        return;
      }

      const childProps = child.props as any;
      const slotType = childProps['data-slot-type'];
      
      if (slotType === 'title' && !title) {
        titleSlot = child;
      } else if (slotType === 'extra' && !extra) {
        extraSlot = child;
      } else if (slotType === 'cover' && !cover) {
        coverSlot = child;
      } else if (slotType === 'actions' && !actions) {
        // 整个 actions slot div 作为单项传给 Ant Design Card。
        // actionsFromChildren=true 触发自动注入 actionsLiMargin:'0px'，
        // 消除 Ant Design li 默认的 margin（paddingSM px 0），与设计器对齐。
        actionsSlot = [child];
        actionsFromChildren = true;
      } else if (slotType === 'body') {
         // body 插槽的内容作为主要 children
         // 注意：AndCardSlotProcessor 会尝试直接返回 body 内容，没有 wrapper
         // 如果有 wrapper，则解包或直接使用
         if (childProps.children) {
            otherChildren.push(childProps.children);
         } else {
            otherChildren.push(child);
         }
      } else {
        otherChildren.push(child);
      }
    });

    return { titleSlot, extraSlot, coverSlot, actionsSlot, otherChildren, actionsFromChildren };
  }, [children, title, extra, cover, actions]);

  // NOTE: actionsLiMargin 是 AntD5 Card 组件 token（默认 `${paddingSM}px 0`，约 8px 0）。
  // 当 actions 来自 children slot（导出代码路径），slot div 本身已包含全部布局样式，
  // AntD li 的额外 margin 会造成高度溢出，需归零以与设计器保持一致。
  // 用户自定义 token 优先级更高（spread 在后）。
  const token = useMemo(() => {
    const base = parseJson(propToken, {});
    if (actionsFromChildren) {
      return { actionsLiMargin: '0px', ...base };
    }
    return base;
  }, [propToken, actionsFromChildren]);

  // 渲染 Card
  const cardElement = (
    <Card
      {...restProps}
      size={normalizedSize}
      title={titleSlot}
      extra={extraSlot}
      cover={coverSlot}
      tabBarExtraContent={tabBarExtraContent}
      actions={actionsSlot.length > 0 ? actionsSlot : undefined}
      tabList={tabList}
      activeTabKey={activeTabKey}
      defaultActiveTabKey={defaultActiveTabKey}
      onTabChange={tabList ? handleTabChange : undefined}
      styles={styles}
    >
      {otherChildren}
    </Card>
  );

  // 应用 Design Token
  if (token && Object.keys(token).length > 0) {
    return (
      <ConfigProvider theme={{ components: { Card: token } }}>
        {cardElement}
      </ConfigProvider>
    );
  }

  return cardElement;
};

export default AndCard;
